// This file serves two distinct and deliberately separate jobs. Do not mix their collections.
//
// 1. `VALID_HTML_TAGS` + `verifyHtmlTag` answer "may a component render itself as this element?"
//    for the `@tag` argument. That list is developer-facing and intentionally permissive: it
//    includes `iframe`, `object`, `embed`, `form`, and `input`, all of which a component is
//    allowed to be but none of which may survive inside a user-supplied string.
//
// 2. The sanitization collections below answer "may this element survive inside a runtime string
//    passed to `@content`?" They are consumed by `utils/spds/sanitize-html`.
//
// Never import `VALID_HTML_TAGS` for sanitization; it would allow every tag the sanitizer exists
// to remove.

// Reference https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements for valid HTML tags
// Note that the list has been curated to exclude deprecated tags such as <acronym> and <applet>, as well as
// valid tags that we do not want to support, such as <script> and <style>
const VALID_HTML_TAGS = Object.freeze([
  'a',
  'abbr',
  'address',
  'area',
  'article',
  'aside',
  'audio',
  'b',
  'blockquote',
  'br',
  'button',
  'canvas',
  'cite',
  'code',
  'data',
  'dd',
  'del',
  'details',
  'dfn',
  'dialog',
  'div',
  'dl',
  'dt',
  'em',
  'embed',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'i',
  'iframe',
  'img',
  'input',
  'ins',
  'label',
  'legend',
  'li',
  'map',
  'mark',
  'menu',
  'menuitem',
  'meter',
  'nav',
  'object',
  'ol',
  'option',
  'p',
  'param',
  'picture',
  'pre',
  'progress',
  'q',
  'samp',
  'section',
  'select',
  'source',
  'span',
  'strong',
  'sub',
  'summary',
  'sup',
  'table',
  'tbody',
  'td',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'tr',
  'track',
  'ul',
  'var',
  'video',
  'wbr',
]);

// ---------------------------------------------------------------------------
// Sanitization collections
// ---------------------------------------------------------------------------

/**
 * Elements dropped **with their entire subtree** under every policy, so their text content cannot
 * leak back out as visible text. These either execute code, switch the parser's namespace, carry
 * their own fetch/navigation surface, or enable DOM clobbering.
 *
 * Form elements are forbidden under both policies: nothing in any repo passes them as a content
 * string, and they bring `formaction`, `autofocus`, and name-based DOM clobbering with them.
 *
 * `svg` is forbidden rather than hardened. It switches parser namespaces and has several routes
 * back into script execution (`foreignObject`, `use`, `xlink:href`); because no SVG element
 * survives, the whole class is designed out rather than defended against.
 */
export const FORBIDDEN_TAGS = Object.freeze(
  new Set([
    'applet',
    'base',
    'button',
    'embed',
    'fieldset',
    'form',
    'frame',
    'frameset',
    'iframe',
    'input',
    'label',
    'link',
    'marquee',
    'math',
    'meta',
    'noscript',
    'object',
    'optgroup',
    'option',
    'script',
    'select',
    'style',
    'svg',
    'template',
    'textarea',
    'title',
  ])
);

/** Inline text-formatting tags, shared by both policies. */
const INLINE_FORMATTING_TAGS = [
  'b',
  'br',
  'em',
  'i',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
  'wbr',
];

/** Block and structural text tags, plus tables, inline semantics, links, and images. */
const RICH_ONLY_TAGS = [
  // Block / structural
  'blockquote',
  'code',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'ul',
  // Tables
  'caption',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  // Inline semantics
  'abbr',
  'cite',
  'del',
  'ins',
  'mark',
  'q',
  'time',
  // Media and links
  'a',
  'img',
];

/** URL schemes accepted by `a[href]` and `img[src]`. Anything else fails closed. */
export const SAFE_URL_SCHEMES = Object.freeze(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Raster data URIs accepted by `img[src]`. `data:image/svg+xml` is deliberately absent: SVG is a
 * script-carrying container, and `svg` is in {@link FORBIDDEN_TAGS} for the same reason.
 */
export const SAFE_IMAGE_DATA_URI = /^data:image\/(?:png|jpe?g|gif|webp)[,;]/;

/** Attributes that require URL scheme validation, keyed by tag. */
export const URL_ATTRIBUTES = Object.freeze({
  a: Object.freeze(new Set(['href'])),
  img: Object.freeze(new Set(['src'])),
});

/**
 * Tooltips. Inline text formatting only and **zero attributes**, matching the component's
 * documented contract ("Tooltips should contain only text. Any HTML included should be for
 * formatting purposes only") and the a11y rule that a tooltip cannot contain interactive content.
 *
 * No `a`, so no URL parsing happens on this path at all and `forceTargetBlank` is meaningless here.
 */
export const STRICT = Object.freeze({
  globalAttributes: Object.freeze(new Set()),
  name: 'strict',
  tagAttributes: Object.freeze({}),
  tags: Object.freeze(new Set(INLINE_FORMATTING_TAGS)),
});

/**
 * Containers and modals. The strict set plus block text structure, tables, links, and images.
 *
 * Attributes are allowlisted **per tag** rather than globally, so `on*` handlers and `style` are
 * dropped by construction rather than by a denylist that could be bypassed.
 */
export const RICH = Object.freeze({
  forceTargetBlank: true,
  globalAttributes: Object.freeze(new Set(['dir', 'lang', 'title'])),
  name: 'rich',
  tagAttributes: Object.freeze({
    a: Object.freeze(new Set(['href', 'rel', 'target'])),
    img: Object.freeze(new Set(['alt', 'height', 'src', 'width'])),
  }),
  tags: Object.freeze(new Set([...INLINE_FORMATTING_TAGS, ...RICH_ONLY_TAGS])),
});

export function verifyHtmlTag(fallback, tag = fallback) {
  let isValid = VALID_HTML_TAGS.includes(tag);

  return isValid ? tag : fallback;
}

export default { verifyHtmlTag };
